import { useEffect, useMemo, useRef, useState } from "react";

const POSTER_DESKTOP = "/hero-poster.webp";
const POSTER_MOBILE = "/hero-poster-mobile.webp";

// 16:9 landscape clips for wide-desktop cycling.
// Ordered lightest -> heaviest. First clip MUST be the lightest so first
// paint is fast.
//   hero-2.mp4 1.4M · hero-1.mp4 3.6M · hero-4.mp4 4.1M ·
//   hero-3.mp4 4.7M · hero-8.mp4 6.2M · hero-6.mp4 6.4M
const DESKTOP_SOURCES: ClipSource[] = [
  { src: "/hero/hero-2.mp4" },
  { src: "/hero/hero-1.mp4" },
  { src: "/hero/hero-4.mp4" },
  { src: "/hero/hero-3.mp4" },
  { src: "/hero/hero-8.mp4" },
  { src: "/hero/hero-6.mp4" },
];

// 9:16 portrait clips for mobile / coarse-pointer viewports.
//
// HOW TO USE:
//   1. Drop your 9:16 .mp4 files in /public/hero/mobile/. Suggested
//      encoding: H.264 high profile, 720x1280, 1.0–1.5 Mbps, faststart
//      (ffmpeg flag -movflags +faststart so the moov atom is at the
//      front and playback starts before the whole file downloads).
//   2. Generate a poster (frame 1) per clip with the helper script:
//        node scripts/scan-mobile-hero.cjs
//      That walks /public/hero/mobile/*.mp4 and writes a matching
//      -poster.webp for each (720x1280, ~25–40 KB).
//   3. Add each clip to the array below. ORDER MATTERS — the first
//      entry is the first clip the user sees, so put the lightest /
//      most impactful one first.
//
// LOADING BEHAVIOR (mobile-cycle tier):
//   • Exactly ONE clip's bytes ever in flight at a time. The next
//     clip's <video> element exists with preload="none" until the
//     current clip hits 50% playback — then we promote it to
//     preload="auto" + .load() in a single beat.
//   • Cross-fade between A and B is 350 ms; the last drawn frame
//     stays visible during the swap so there's never a black flash.
//   • Save-Data / 2g users skip cycling and see the poster image
//     only. 3g users get the first clip on infinite loop.
//
// FALLBACK: an empty MOBILE_SOURCES array makes mobile viewers fall
// back to a single looping hero-2.mp4 (current behavior). The deploy
// never breaks even before you've uploaded clips.
type ClipSource = { src: string; poster?: string };
const MOBILE_SOURCES: ClipSource[] = [
  { src: "/hero/mobile/clip-1.mp4", poster: "/hero/mobile/clip-1-poster.webp" },
  { src: "/hero/mobile/clip-2.mp4", poster: "/hero/mobile/clip-2-poster.webp" },
  { src: "/hero/mobile/clip-3.mp4", poster: "/hero/mobile/clip-3-poster.webp" },
  { src: "/hero/mobile/clip-4.mp4", poster: "/hero/mobile/clip-4-poster.webp" },
  { src: "/hero/mobile/clip-5.mp4", poster: "/hero/mobile/clip-5-poster.webp" },
  { src: "/hero/mobile/clip-6.mp4", poster: "/hero/mobile/clip-6-poster.webp" },
  { src: "/hero/mobile/clip-7.mp4", poster: "/hero/mobile/clip-7-poster.webp" },
  { src: "/hero/mobile/clip-8.mp4", poster: "/hero/mobile/clip-8-poster.webp" },
  { src: "/hero/mobile/clip-9.mp4", poster: "/hero/mobile/clip-9-poster.webp" },
];

// Connection / viewport tiering. Pick once per mount.
//   "skip"          — no video. Show poster only.
//                     (2g, slow-2g, save-data, prefers-reduced-motion)
//   "mobile-cycle"  — A/B cycle through MOBILE_SOURCES on coarse pointer
//                     or narrow viewport. Only used if MOBILE_SOURCES
//                     has entries. (4g+ mobile / tablet)
//   "single"        — ONE looping clip with the lightest desktop source.
//                     Fallback when MOBILE_SOURCES is empty, OR on 3g.
//   "full"          — A/B cycle through DESKTOP_SOURCES. (wifi/4g + wide)
type Tier = "skip" | "mobile-cycle" | "single" | "full";

const pickTier = (): Tier => {
  if (typeof window === "undefined") return "single";
  // @ts-expect-error - non-standard
  const c = (typeof navigator !== "undefined" ? navigator.connection : null) as
    | { saveData?: boolean; effectiveType?: string }
    | null;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "skip";
  if (c?.saveData) return "skip";
  if (typeof c?.effectiveType === "string" && /^(slow-2g|2g)$/.test(c.effectiveType)) return "skip";
  const coarse = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  const narrow = window.innerWidth < 768;
  // 3g — single looping clip, no cycle to save bandwidth.
  if (typeof c?.effectiveType === "string" && c.effectiveType === "3g") return "single";
  if (coarse || narrow) {
    // Mobile / coarse pointer. If user has uploaded portrait clips,
    // cycle them. Otherwise fall back to single looping landscape clip.
    return MOBILE_SOURCES.length > 0 ? "mobile-cycle" : "single";
  }
  return "full";
};

/**
 * Hero video background — four branches by tier:
 *
 *   skip          poster image only, no <video>.
 *                 (2g, save-data, prefers-reduced-motion)
 *
 *   mobile-cycle  A/B cross-fade through 9:16 portrait clips in
 *                 MOBILE_SOURCES. Back buffer stays preload="none"
 *                 until current clip is halfway through. Exactly ONE
 *                 clip's bytes in flight at a time.
 *                 (mobile / coarse pointer with 4g+, if you've
 *                 uploaded portrait clips)
 *
 *   single        ONE <video> looping the lightest desktop clip. No
 *                 cycle, no buffer.
 *                 (mobile fallback when MOBILE_SOURCES is empty;
 *                 also 3g everywhere)
 *
 *   full          A/B cycle through 16:9 landscape clips in
 *                 DESKTOP_SOURCES, eager buffer warming.
 *                 (wifi/4g + wide viewport)
 *
 * The poster image is set on every <video> element so the first paint
 * is always a real frame, never black.
 */
const HeroVideoBG = () => {
  const tier = useMemo(pickTier, []);
  const isMobile = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    [],
  );
  const globalPoster = isMobile ? POSTER_MOBILE : POSTER_DESKTOP;

  // For mobile-cycle: choose between MOBILE_SOURCES (real portrait clips
  // the user uploaded) or DESKTOP_SOURCES as a defensive empty-array
  // fallback. The pickTier above already gates on MOBILE_SOURCES.length,
  // so this should never use DESKTOP here — defensive nonetheless.
  const cycleSources = tier === "mobile-cycle" ? MOBILE_SOURCES : DESKTOP_SOURCES;

  // A/B state. Both clip indexes (0-based into cycleSources) live
  // separately so the inactive buffer can race ahead.
  const [active, setActive] = useState<0 | 1>(0);
  const [idxA, setIdxA] = useState(0);
  const [idxB, setIdxB] = useState(1 % Math.max(1, cycleSources.length));
  const [warmBuffer, setWarmBuffer] = useState(false);
  const refA = useRef<HTMLVideoElement>(null);
  const refB = useRef<HTMLVideoElement>(null);
  const cursor = useRef(2);
  const activeRef = useRef<0 | 1>(0);

  const playSafely = (el: HTMLVideoElement | null) => {
    if (!el) return;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };

  // FULL tier: warm the back buffer as soon as the first clip is
  // playable. Desktop wifi has the bandwidth.
  useEffect(() => {
    if (tier !== "full") return;
    const el = refA.current;
    if (!el) return;
    const ready = () => {
      setWarmBuffer(true);
      const b = refB.current;
      if (b) {
        try { b.load(); } catch {}
      }
    };
    if (el.readyState >= 2) { ready(); return; }
    el.addEventListener("loadeddata", ready, { once: true });
    el.addEventListener("canplay", ready, { once: true });
    return () => {
      el.removeEventListener("loadeddata", ready);
      el.removeEventListener("canplay", ready);
    };
  }, [tier]);

  // MOBILE-CYCLE tier: do NOT warm the back buffer eagerly. Wait until
  // the current clip is past 50% before promoting buffer's preload to
  // "auto" and calling .load(). This guarantees only ONE clip's bytes
  // are in flight at any moment.
  useEffect(() => {
    if (tier !== "mobile-cycle") return;
    const el = refA.current;
    if (!el) return;
    let armed = false;
    const onTime = () => {
      if (armed) return;
      if (!el.duration || el.duration === Infinity) return;
      if (el.currentTime / el.duration >= 0.5) {
        armed = true;
        setWarmBuffer(true);
        const b = refB.current;
        if (b) {
          try { b.load(); } catch {}
        }
      }
    };
    el.addEventListener("timeupdate", onTime);
    return () => el.removeEventListener("timeupdate", onTime);
  }, [tier, idxA]);

  const swapTo = (which: 0 | 1) => {
    activeRef.current = which;
    setActive(which);
    playSafely(which === 0 ? refA.current : refB.current);
  };

  const queueNextOn = (which: 0 | 1) => {
    const len = cycleSources.length;
    if (len === 0) return;
    const nextIdx = cursor.current % len;
    cursor.current = (cursor.current + 1) % len;
    if (which === 0) setIdxA(nextIdx);
    else setIdxB(nextIdx);
    // For mobile-cycle, reset the warmBuffer flag so the next
    // buffer-warm cycle starts fresh.
    if (tier === "mobile-cycle") setWarmBuffer(false);
  };

  const handleEnded = (which: 0 | 1) => {
    const other: 0 | 1 = which === 0 ? 1 : 0;
    const otherEl = other === 0 ? refA.current : refB.current;
    const ready = (otherEl?.readyState ?? 0) >= 3;

    if (ready) {
      try { if (otherEl) otherEl.currentTime = 0; } catch {}
      swapTo(other);
      queueNextOn(which);
    } else {
      // Back buffer not ready yet (slow mobile or longer clip):
      // restart the current clip to bridge the gap, swap when ready.
      const currentEl = which === 0 ? refA.current : refB.current;
      if (currentEl) {
        try { currentEl.currentTime = 0; } catch {}
        playSafely(currentEl);
      }
      if (otherEl) {
        const onReady = () => {
          otherEl.removeEventListener("canplay", onReady);
          if (activeRef.current === which) {
            try { otherEl.currentTime = 0; } catch {}
            swapTo(other);
            queueNextOn(which);
          }
        };
        otherEl.addEventListener("canplay", onReady);
      }
    }
  };

  const baseStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  };

  const scrim = (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(180deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.18) 45%, rgba(0,0,0,0.48) 100%)",
      }}
    />
  );

  const wrapStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: 0,
  };

  // ─── Tier: skip — poster only, no <video> ────────────────────────
  if (tier === "skip") {
    return (
      <div aria-hidden style={wrapStyle}>
        <img
          src={globalPoster}
          alt=""
          aria-hidden
          decoding="async"
          fetchPriority="high"
          style={baseStyle}
        />
        {scrim}
      </div>
    );
  }

  // ─── Tier: single — one looping <video>, no A/B buffer ────────────
  if (tier === "single") {
    return (
      <div aria-hidden style={wrapStyle}>
        <video
          ref={refA}
          src={DESKTOP_SOURCES[0].src}
          poster={globalPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlay={() => playSafely(refA.current)}
          style={baseStyle}
        />
        {scrim}
      </div>
    );
  }

  // ─── Tiers: mobile-cycle and full ────────────────────────────────
  // Both use the same A/B element shape. They differ in:
  //   * source pool (cycleSources, set above)
  //   * back-buffer warming (handled by the per-tier useEffect above)
  const srcA = cycleSources[idxA]?.src;
  const srcB = cycleSources[idxB]?.src;
  const posterA = cycleSources[idxA]?.poster ?? globalPoster;
  const posterB = cycleSources[idxB]?.poster ?? globalPoster;
  const preloadA = "auto";
  const preloadB = warmBuffer ? "auto" : "none";

  return (
    <div aria-hidden style={wrapStyle}>
      <video
        ref={refA}
        key={srcA}
        src={srcA}
        poster={posterA}
        autoPlay
        muted
        playsInline
        preload={preloadA}
        onEnded={() => handleEnded(0)}
        onCanPlay={() => playSafely(refA.current)}
        style={{
          ...baseStyle,
          opacity: active === 0 ? 1 : 0,
          transition: "opacity .35s ease",
        }}
      />
      <video
        ref={refB}
        key={srcB}
        src={srcB}
        poster={posterB}
        muted
        playsInline
        preload={preloadB}
        onEnded={() => handleEnded(1)}
        style={{
          ...baseStyle,
          opacity: active === 1 ? 1 : 0,
          transition: "opacity .35s ease",
        }}
      />
      {scrim}
    </div>
  );
};

export default HeroVideoBG;
